/** Wireframe diagrams for concrete estimator shapes — labels show live input values. */

const AREA_UNIT_LABELS = {
    sqft: 'sq ft',
    m2: 'm²',
    sqyd: 'sq yd',
};

function formatDimValue(value) {
    const n = parseFloat(value);
    if (!Number.isFinite(n)) return '';
    if (Number.isInteger(n)) return String(n);
    return String(parseFloat(n.toFixed(3)));
}

function dimCaption(dims, key, symbol, { isCount = false } = {}) {
    const raw = dims?.[key];
    const unit = dims?.[`${key}_unit`] || 'm';
    const formatted = formatDimValue(raw);
    if (!formatted) return symbol;
    if (isCount) return `${symbol} ${formatted}`;
    return `${symbol} ${formatted}${unit}`;
}

function DimText({ dims, dimKey, symbol, x, y, fontSize = 12, isCount = false }) {
    const caption = dimCaption(dims, dimKey, symbol, { isCount });
    return (
        <text x={x} y={y} fill="#0090D8" fontSize={fontSize} fontWeight="700">
            {caption}
        </text>
    );
}

function SlabDiagram({ dims = {} }) {
    const byArea = (dims.measure_by || 'lwh') === 'area';
    const areaUnit = dims.area_unit || 'sqft';
    const areaLabel = AREA_UNIT_LABELS[areaUnit] || areaUnit;
    const areaVal = formatDimValue(dims.area);
    const areaCaption = areaVal ? `area ${areaVal} ${areaLabel}` : 'area';

    return (
        <svg viewBox="0 0 200 140" className="estimator-shape-svg" aria-hidden>
            <polygon points="40,90 120,90 140,70 60,70" fill="none" stroke="#333" strokeWidth="1.5" />
            <polygon points="120,90 140,70 140,30 120,50" fill="none" stroke="#333" strokeWidth="1.5" />
            <polygon points="40,90 60,70 60,30 40,50" fill="none" stroke="#333" strokeWidth="1.5" />
            <line x1="40" y1="90" x2="40" y2="50" stroke="#333" strokeWidth="1.5" strokeDasharray="4 3" />
            <line x1="120" y1="90" x2="120" y2="50" stroke="#333" strokeWidth="1.5" strokeDasharray="4 3" />
            <line x1="140" y1="70" x2="140" y2="30" stroke="#333" strokeWidth="1.5" strokeDasharray="4 3" />
            {byArea ? (
                <text x={52} y={108} fill="#0090D8" fontSize={12} fontWeight="700">{areaCaption}</text>
            ) : (
                <>
                    <DimText dims={dims} dimKey="length" symbol="l" x={72} y={108} fontSize={13} />
                    <DimText dims={dims} dimKey="width" symbol="w" x={148} y={52} fontSize={13} />
                </>
            )}
            <DimText dims={dims} dimKey="height" symbol="h" x={8} y={72} fontSize={13} />
        </svg>
    );
}

function ColumnDiagram({ dims = {} }) {
    return (
        <svg viewBox="0 0 200 140" className="estimator-shape-svg" aria-hidden>
            <ellipse cx="100" cy="35" rx="35" ry="12" fill="none" stroke="#333" strokeWidth="1.5" />
            <line x1="65" y1="35" x2="65" y2="105" stroke="#333" strokeWidth="1.5" />
            <line x1="135" y1="35" x2="135" y2="105" stroke="#333" strokeWidth="1.5" />
            <ellipse cx="100" cy="105" rx="35" ry="12" fill="none" stroke="#333" strokeWidth="1.5" />
            <line x1="65" y1="35" x2="135" y2="35" stroke="#333" strokeWidth="1" strokeDasharray="4 3" />
            <DimText dims={dims} dimKey="diameter" symbol="d" x={142} y={38} />
            <DimText dims={dims} dimKey="height" symbol="h" x={142} y={78} />
        </svg>
    );
}

function TubeDiagram({ dims = {} }) {
    return (
        <svg viewBox="0 0 200 140" className="estimator-shape-svg" aria-hidden>
            <ellipse cx="100" cy="35" rx="48" ry="14" fill="none" stroke="#333" strokeWidth="1.5" />
            <ellipse cx="100" cy="35" rx="22" ry="7" fill="none" stroke="#333" strokeWidth="1" strokeDasharray="3 2" />
            <line x1="52" y1="35" x2="52" y2="105" stroke="#333" strokeWidth="1.5" />
            <line x1="148" y1="35" x2="148" y2="105" stroke="#333" strokeWidth="1.5" />
            <ellipse cx="100" cy="105" rx="48" ry="14" fill="none" stroke="#333" strokeWidth="1.5" />
            <ellipse cx="100" cy="105" rx="22" ry="7" fill="none" stroke="#333" strokeWidth="1" strokeDasharray="3 2" />
            <DimText dims={dims} dimKey="outer_diameter" symbol="D" x={8} y={32} fontSize={11} />
            <DimText dims={dims} dimKey="inner_diameter" symbol="d" x={8} y={48} fontSize={11} />
            <DimText dims={dims} dimKey="height" symbol="h" x={155} y={72} />
        </svg>
    );
}

function CurbDiagram({ dims = {} }) {
    return (
        <svg viewBox="0 0 200 140" className="estimator-shape-svg" aria-hidden>
            <polyline points="30,100 30,55 70,55 70,75 130,75 130,100" fill="none" stroke="#333" strokeWidth="1.5" />
            <line x1="30" y1="100" x2="130" y2="100" stroke="#333" strokeWidth="1.5" />
            <DimText dims={dims} dimKey="length" symbol="l" x={42} y={118} />
            <DimText dims={dims} dimKey="curb_height" symbol="h" x={8} y={68} fontSize={11} />
            <DimText dims={dims} dimKey="curb_depth" symbol="d" x={38} y={50} fontSize={11} />
        </svg>
    );
}

function StairsDiagram({ dims = {} }) {
    return (
        <svg viewBox="0 0 200 140" className="estimator-shape-svg" aria-hidden>
            <polyline points="35,105 35,85 55,85 55,65 75,65 75,45 95,45 95,105" fill="none" stroke="#333" strokeWidth="1.5" />
            <line x1="35" y1="105" x2="130" y2="105" stroke="#333" strokeWidth="1.5" />
            <line x1="95" y1="45" x2="130" y2="45" stroke="#333" strokeWidth="1.5" strokeDasharray="4 3" />
            <line x1="130" y1="45" x2="130" y2="105" stroke="#333" strokeWidth="1.5" strokeDasharray="4 3" />
            <DimText dims={dims} dimKey="run" symbol="run" x={42} y={98} fontSize={11} />
            <DimText dims={dims} dimKey="rise" symbol="rise" x={4} y={78} fontSize={11} />
            <DimText dims={dims} dimKey="width" symbol="w" x={108} y={38} fontSize={11} />
            <DimText dims={dims} dimKey="num_risers" symbol="n" x={108} y={118} fontSize={11} isCount />
        </svg>
    );
}

const DIAGRAMS = {
    slab_wall_footing: SlabDiagram,
    round_column_hole: ColumnDiagram,
    circular_slab_tube: TubeDiagram,
    curb_gutter: CurbDiagram,
    stairs: StairsDiagram,
};

export default function EstimatorShapeDiagram({ shapeType, dimensions = {} }) {
    const Diagram = DIAGRAMS[shapeType] || SlabDiagram;
    return (
        <div className="estimator-calc-diagram">
            <Diagram dims={dimensions} />
        </div>
    );
}
